import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { v1Get, v1Put, v1Post } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Save, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { IQ_CATEGORIES } from '@/lib/reportConstants';
import {
  TECH_DIFFICULTIES,
  matchesTechQuestionFilters,
  formatApiError,
} from './pipelineUtils';
import {
  TechQuestionMeta,
  PipelineFilterMultiSelect,
  clampDurationSeconds,
  DurationMinsSecs,
  SelectRandomQuestionsDialog,
} from './PipelineBindingShared';
import { Gated } from '@/auth/permissions';

export function VideoBotBinding({ pipelineId, pipelineLabel }) {
  const qc = useQueryClient();
  const { data: binding } = useQuery({
    queryKey: ['pipeline-vb-binding', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/videobot-questions`),
  });
  const { data: allQs } = useQuery({
    queryKey: ['v1-videobot-qs-all'],
    queryFn: () => v1Get('/questions/videobot'),
  });
  const [draft, setDraft] = useState(null);
  const [randomOpen, setRandomOpen] = useState(false);
  const [randomCount, setRandomCount] = useState('1');
  const [randomLoading, setRandomLoading] = useState(false);
  // Hydrate draft when binding loads or pipeline changes
  React.useEffect(() => {
    if (binding) {
      setDraft(binding.question_ids);
    }
  }, [binding, pipelineId]);
  const activeQs = (allQs || []).filter((q) => q.status === 'active');
  if (!binding || !allQs || draft === null) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">Loading Video Bot question bindings…</div>
    );
  }

  const inSet = new Set(draft);
  const ordered = draft.map((qid) => activeQs.find((q) => q.id === qid)).filter(Boolean);
  const available = activeQs.filter((q) => !inSet.has(q.id));
  const randomMax = Math.min(20, activeQs.length);

  const selectRandom = async () => {
    if (draft.length && !window.confirm('Replace the current draft with a new random selection?')) return;
    setRandomLoading(true);
    try {
      const out = await v1Post(`/pipelines/${pipelineId}/videobot-questions/sample`, {
        count: Number(randomCount),
      });
      setDraft(out.question_ids || []);
      setRandomOpen(false);
      toast.success(`Selected ${out.count || 0} Video Bot questions`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Random selection failed'));
    } finally {
      setRandomLoading(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setDraft(next.map((q) => q.id));
  };

  const save = async () => {
    try {
      await v1Put(`/pipelines/${pipelineId}/videobot-questions`, { question_ids: draft });
      qc.invalidateQueries({ queryKey: ['pipeline-vb-binding', pipelineId] });
      toast.success('Video Bot question binding saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(binding.question_ids);

  return (
    <div className="mt-5 rounded-xl border bg-secondary/40 p-4" data-testid="pipeline-videobot-binding">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Video Bot questions for {pipelineLabel}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Candidates assigned to this pipeline will be asked these questions, in this order.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Gated need="pipeline.edit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setRandomOpen(true)}
              disabled={randomMax < 1}
              data-testid="pipeline-videobot-random-open"
            >
              Select random questions
            </Button>
            <Gated need="bank.videobot.view">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/portal/settings/questions/videobot"><FileText className="h-3.5 w-3.5" /> Manage bank</Link>
              </Button>
            </Gated>
            <Button size="sm" onClick={save} disabled={!dirty} className="gap-1.5" data-testid="pipeline-videobot-binding-save"><Save className="h-3.5 w-3.5" /> Save binding</Button>
          </Gated>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In this pipeline ({ordered.length})</p>
          {ordered.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No questions yet. Add some from the right panel.</p>
          ) : (
            <ol className="mt-2 space-y-1.5">
              {ordered.map((q, i) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2" data-testid={`pipeline-vb-bound-${i}`}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{i + 1}</span>
                  <span className="line-clamp-2 flex-1 text-xs leading-snug">{q.text}</span>
                  <div className="flex shrink-0 flex-col gap-1 text-muted-foreground">
                    <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-[10px] disabled:opacity-30">▲</button>
                    <button onClick={() => move(i, i + 1)} disabled={i === ordered.length - 1} className="text-[10px] disabled:opacity-30">▼</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(draft.filter((id) => id !== q.id))} className="-mr-1 -mt-1 h-6 w-6 p-0"><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Available active questions ({available.length})</p>
          {available.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">Every active question is already bound.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {available.map((q) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2">
                  <span className="line-clamp-2 flex-1 text-xs leading-snug">{q.text}</span>
                  <Button variant="ghost" size="sm" onClick={() => setDraft([...draft, q.id])} className="gap-1 text-[11px]" data-testid={`pipeline-vb-add-${q.id}`}><Plus className="h-3 w-3" /> Add</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <SelectRandomQuestionsDialog
        open={randomOpen}
        onOpenChange={setRandomOpen}
        title={`Select random Video Bot questions for ${pipelineLabel}`}
        description="Choose how many active questions to pull into this pipeline draft. Save binding to keep the result."
        count={randomCount}
        setCount={setRandomCount}
        maxCount={randomMax}
        onConfirm={selectRandom}
        loading={randomLoading}
        testIdPrefix="pipeline-videobot-random"
      />
    </div>
  );
}


export function McqBinding({ pipelineId, pipelineLabel }) {
  const qc = useQueryClient();
  const { data: binding } = useQuery({
    queryKey: ['pipeline-mcq-binding', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/mcq-questions`),
  });
  const { data: allQs } = useQuery({
    queryKey: ['v1-mcq-qs-all'],
    queryFn: () => v1Get('/questions/mcq'),
  });
  const [draft, setDraft] = useState(null);
  const [totalSecs, setTotalSecs] = useState(null);
  const [topicFilter, setTopicFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState([]);
  const [randomOpen, setRandomOpen] = useState(false);
  const [randomCount, setRandomCount] = useState('1');
  const [randomLoading, setRandomLoading] = useState(false);
  React.useEffect(() => {
    if (binding) {
      setDraft(binding.question_ids);
      setTotalSecs(binding.mcq_total_seconds);
    }
  }, [binding, pipelineId]);
  React.useEffect(() => {
    setTopicFilter('');
    setDifficultyFilter([]);
  }, [pipelineId]);
  const activeQs = (allQs || []).filter((q) => q.status === 'active');
  if (!binding || !allQs || draft === null || totalSecs === null) {
    return <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">Loading IQ Assessment question bindings…</div>;
  }

  const inSet = new Set(draft);
  const ordered = draft.map((qid) => activeQs.find((q) => q.id === qid)).filter(Boolean);
  const available = activeQs
    .filter((q) => !inSet.has(q.id))
    .filter((q) => !topicFilter || q.topic === topicFilter)
    .filter((q) => matchesTechQuestionFilters(q, [], difficultyFilter));
  const randomMax = Math.min(100, activeQs.length);
  const difficultyOptions = TECH_DIFFICULTIES.map((d) => ({
    value: d,
    label: d.charAt(0).toUpperCase() + d.slice(1),
  }));
  const filtersActive = !!topicFilter || difficultyFilter.length > 0;
  const selectRandom = async () => {
    if (draft.length && !window.confirm('Replace the current draft with a new random selection?')) return;
    setRandomLoading(true);
    try {
      const out = await v1Post(`/pipelines/${pipelineId}/mcq-questions/sample`, {
        count: Number(randomCount),
      });
      setDraft(out.question_ids || []);
      setRandomOpen(false);
      toast.success(`Selected ${out.count || 0} IQ Assessment questions`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Random selection failed'));
    } finally {
      setRandomLoading(false);
    }
  };
  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setDraft(next.map((q) => q.id));
  };
  const save = async () => {
    try {
      await v1Put(`/pipelines/${pipelineId}/mcq-questions`, {
        question_ids: draft, mcq_total_seconds: clampDurationSeconds(totalSecs),
      });
      qc.invalidateQueries({ queryKey: ['pipeline-mcq-binding', pipelineId] });
      toast.success('IQ Assessment binding saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(binding.question_ids) || totalSecs !== binding.mcq_total_seconds;
  return (
    <div className="mt-5 rounded-xl border bg-secondary/40 p-4" data-testid="pipeline-mcq-binding">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">IQ Assessment questions for {pipelineLabel}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Single aggregate timer. Candidates see questions in this exact order.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DurationMinsSecs value={totalSecs} onChange={setTotalSecs} testIdPrefix="pipeline-mcq" />
          <Gated need="pipeline.edit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setRandomOpen(true)}
              disabled={randomMax < 1}
              data-testid="pipeline-mcq-random-open"
            >
              Select random questions
            </Button>
            <Gated need="bank.psa.view">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/portal/settings/questions/mcq"><FileText className="h-3.5 w-3.5" /> Manage bank</Link>
              </Button>
            </Gated>
            <Button size="sm" onClick={save} disabled={!dirty} className="gap-1.5" data-testid="pipeline-mcq-binding-save"><Save className="h-3.5 w-3.5" /> Save binding</Button>
          </Gated>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In this pipeline ({ordered.length})</p>
          {ordered.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No questions yet. Add some from the right panel.</p>
          ) : (
            <ol className="mt-2 space-y-1.5">
              {ordered.map((q, i) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2" data-testid={`pipeline-mcq-bound-${i}`}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <div className="flex shrink-0 flex-col gap-1 text-muted-foreground">
                    <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-[10px] disabled:opacity-30">▲</button>
                    <button onClick={() => move(i, i + 1)} disabled={i === ordered.length - 1} className="text-[10px] disabled:opacity-30">▼</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(draft.filter((id) => id !== q.id))} className="-mr-1 -mt-1 h-6 w-6 p-0"><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Available active questions ({available.length})</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Select value={topicFilter || '__all__'} onValueChange={(v) => setTopicFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-7 w-[180px] text-[11px]" data-testid="pipeline-mcq-topic-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {IQ_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PipelineFilterMultiSelect
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={setDifficultyFilter}
                placeholder="All difficulties"
                testId="pipeline-mcq-difficulty-filter"
                compact
              />
              {filtersActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => { setTopicFilter(''); setDifficultyFilter([]); }}
                  data-testid="pipeline-mcq-filter-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
          {available.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {filtersActive
                ? 'No active questions match the selected category and difficulty filters.'
                : 'Every active question is already bound.'}
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {available.map((q) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2">
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft([...draft, q.id])} className="gap-1 text-[11px]" data-testid={`pipeline-mcq-add-${q.id}`}><Plus className="h-3 w-3" /> Add</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <SelectRandomQuestionsDialog
        open={randomOpen}
        onOpenChange={setRandomOpen}
        title={`Select random IQ Assessment questions for ${pipelineLabel}`}
        description="A balanced sample is drawn from all active IQ Assessment questions, regardless of the manual filters on the right."
        count={randomCount}
        setCount={setRandomCount}
        maxCount={randomMax}
        onConfirm={selectRandom}
        loading={randomLoading}
        testIdPrefix="pipeline-mcq-random"
      />
    </div>
  );
}

export function TechAssessmentBinding({ pipelineId, pipelineLabel, roleIds = [], departmentId }) {
  const qc = useQueryClient();
  const { data: binding, isError: bindingError, error: bindingErr } = useQuery({
    queryKey: ['pipeline-tech-binding', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/tech-questions`),
    enabled: !!pipelineId,
    retry: false,
  });
  const roleKey = (roleIds || []).slice().sort().join(',');
  const techQueryKey = ['v1-tech-qs-pipeline', roleKey, departmentId];
  const { data: allQs, isError: questionsError, error: questionsErr } = useQuery({
    queryKey: techQueryKey,
    queryFn: () => {
      const params = new URLSearchParams({ status: 'active' });
      if (departmentId) params.set('department_id', departmentId);
      // Single role: server-side filter. Multiple: fetch dept-scoped and filter client-side.
      if ((roleIds || []).length === 1) params.set('role_id', roleIds[0]);
      return v1Get(`/questions/tech?${params.toString()}`);
    },
    enabled: !!pipelineId,
    retry: false,
  });
  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', departmentId],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${departmentId}`),
    enabled: !!pipelineId && !!departmentId,
  });
  const [draft, setDraft] = useState(null);
  const [totalSecs, setTotalSecs] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [difficultyFilter, setDifficultyFilter] = useState([]);
  const [randomOpen, setRandomOpen] = useState(false);
  const [randomCount, setRandomCount] = useState('1');
  const [randomLoading, setRandomLoading] = useState(false);
  React.useEffect(() => {
    if (binding) {
      setDraft(binding.question_ids ?? []);
      setTotalSecs(binding.tech_total_seconds ?? 1800);
    }
  }, [binding, pipelineId]);
  React.useEffect(() => {
    setCategoryFilter([]);
    setDifficultyFilter([]);
  }, [pipelineId, departmentId]);

  if (!pipelineId) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Save the pipeline first, then configure Tech Assessment questions.
      </div>
    );
  }
  if (bindingError || questionsError) {
    const detail = bindingError
      ? formatApiError(bindingErr?.response?.data?.detail, 'Could not load Tech Assessment bindings')
      : formatApiError(questionsErr?.response?.data?.detail, 'Could not load Tech Assessment questions');
    return (
      <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50/40 p-4 text-xs text-rose-800" data-testid="pipeline-tech-binding-error">
        <p className="font-semibold">Failed to load Tech Assessment configuration</p>
        <p className="mt-1">{detail}</p>
        <p className="mt-2 text-[11px] text-rose-700/80">
          If this says the endpoint was retired (410), restart the backend so it includes the latest pipeline routes.
        </p>
      </div>
    );
  }

  if (!binding || allQs === undefined || draft === null || totalSecs === null) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Loading Tech Assessment question bindings…
      </div>
    );
  }

  const draftIds = draft;
  const pipelineRoleSet = new Set(roleIds || []);
  const activeQs = (allQs || []).filter((q) => {
    if (q.status !== 'active') return false;
    if (!pipelineRoleSet.size) return true;
    const qRoles = Array.isArray(q.role_ids) && q.role_ids.length
      ? q.role_ids
      : (q.role_id ? [q.role_id] : []);
    return qRoles.some((rid) => pipelineRoleSet.has(rid));
  });

  const inSet = new Set(draftIds);
  const ordered = draftIds.map((qid) => activeQs.find((q) => q.id === qid)).filter(Boolean);
  const available = activeQs
    .filter((q) => !inSet.has(q.id))
    .filter((q) => matchesTechQuestionFilters(q, categoryFilter, difficultyFilter));
  const randomMax = Math.min(100, activeQs.length);
  const categoryOptions = workCategories.map((c) => ({ value: c.label, label: c.label }));
  const difficultyOptions = TECH_DIFFICULTIES.map((d) => ({
    value: d,
    label: d.charAt(0).toUpperCase() + d.slice(1),
  }));
  const filtersActive = categoryFilter.length > 0 || difficultyFilter.length > 0;
  const selectRandom = async () => {
    if (draftIds.length && !window.confirm('Replace the current draft with a new random selection?')) return;
    setRandomLoading(true);
    try {
      const out = await v1Post(`/pipelines/${pipelineId}/tech-questions/sample`, {
        count: Number(randomCount),
      });
      setDraft(out.question_ids || []);
      setRandomOpen(false);
      toast.success(`Selected ${out.count || 0} Tech Assessment questions`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Random selection failed'));
    } finally {
      setRandomLoading(false);
    }
  };
  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setDraft(next.map((q) => q.id));
  };
  const save = async () => {
    try {
      await v1Put(`/pipelines/${pipelineId}/tech-questions`, {
        question_ids: draftIds,
        tech_total_seconds: clampDurationSeconds(totalSecs),
      });
      qc.invalidateQueries({ queryKey: ['pipeline-tech-binding', pipelineId] });
      toast.success('Tech Assessment binding saved');
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const dirty = (
    JSON.stringify(draftIds) !== JSON.stringify(binding?.question_ids ?? [])
    || totalSecs !== (binding?.tech_total_seconds ?? 1800)
  );

  return (
    <div className="mt-5 rounded-xl border bg-secondary/40 p-4" data-testid="pipeline-tech-binding">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Tech Assessment questions for {pipelineLabel}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Candidates assigned to this pipeline will see these questions in this order.
            {(roleIds || []).length && departmentId
              ? ' Showing active questions matching this pipeline’s roles and department.'
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DurationMinsSecs value={totalSecs} onChange={setTotalSecs} testIdPrefix="pipeline-tech" />
          <Gated need="pipeline.edit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setRandomOpen(true)}
              disabled={randomMax < 1}
              data-testid="pipeline-tech-random-open"
            >
              Select random questions
            </Button>
            <Gated need="bank.tech.view">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/portal/settings/questions/tech"><FileText className="h-3.5 w-3.5" /> Manage bank</Link>
              </Button>
            </Gated>
            <Button size="sm" onClick={save} disabled={!dirty} className="gap-1.5" data-testid="pipeline-tech-binding-save">
              <Save className="h-3.5 w-3.5" /> Save binding
            </Button>
          </Gated>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In this pipeline ({ordered.length})</p>
          {ordered.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No questions yet. Add some from the right panel.</p>
          ) : (
            <ol className="mt-2 space-y-1.5">
              {ordered.map((q, i) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2" data-testid={`pipeline-tech-bound-${i}`}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <div className="flex shrink-0 flex-col gap-1 text-muted-foreground">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="text-[10px] disabled:opacity-30">▲</button>
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === ordered.length - 1} className="text-[10px] disabled:opacity-30">▼</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(draftIds.filter((id) => id !== q.id))} className="-mr-1 -mt-1 h-6 w-6 p-0"><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Available active questions ({available.length})</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <PipelineFilterMultiSelect
                options={categoryOptions}
                value={categoryFilter}
                onChange={setCategoryFilter}
                placeholder="All categories"
                disabled={!departmentId}
                testId="pipeline-tech-category-filter"
                compact
              />
              <PipelineFilterMultiSelect
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={setDifficultyFilter}
                placeholder="All difficulties"
                testId="pipeline-tech-difficulty-filter"
                compact
              />
              {filtersActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => { setCategoryFilter([]); setDifficultyFilter([]); }}
                  data-testid="pipeline-tech-filter-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
          {available.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {filtersActive
                ? 'No active questions match the selected category and difficulty filters.'
                : (
                  <>
                    No matching active questions.
                    <Gated need="bank.tech.view">
                      {' '}
                      <Link to="/portal/settings/questions/tech" className="font-semibold text-primary underline">Add some in the Tech Assessment bank</Link>
                      .
                    </Gated>
                  </>
                )}
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {available.map((q) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2">
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft([...draftIds, q.id])} className="gap-1 text-[11px]" data-testid={`pipeline-tech-add-${q.id}`}><Plus className="h-3 w-3" /> Add</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <SelectRandomQuestionsDialog
        open={randomOpen}
        onOpenChange={setRandomOpen}
        title={`Select random Tech Assessment questions for ${pipelineLabel}`}
        description="A balanced sample is drawn from active questions that match this pipeline’s department and roles."
        count={randomCount}
        setCount={setRandomCount}
        maxCount={randomMax}
        onConfirm={selectRandom}
        loading={randomLoading}
        testIdPrefix="pipeline-tech-random"
      />
    </div>
  );
}
